import { fullLabel } from "./WhereItIs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProblemMove } from "@/lib/useProblemMove";
import { AuthImg } from "@/components/AuthImg";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Package, Loader2, StickyNote, ArrowRightLeft, MapPin, Boxes, PackageOpen, Smartphone, Check } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";

// DESK problem-move resolver drawer — SAME context + fixes as the Floor card, but with
// typed destination entry (no camera). Fully capable at a desk; "Fix at the bin" hands
// off to the tablet resolver when the tech is standing at the tote.
export function ProblemMoveDrawer({ id, open, onClose, onResolved }) {
  const navigate = useNavigate();
  const { detail, loading, busy, doFix, doResolve } = useProblemMove(open ? id : null);
  const [dest, setDest] = useState("");

  const finish = (msg) => { toast.success(msg); onResolved?.(); onClose?.(); };
  const fail = (msg) => toast.error(msg);

  const move = async () => {
    const code = dest.trim();
    if (!code) { toast.error("Enter the correct destination bin code."); return; }
    try { const r = await doFix("move_to", code); r.ok ? finish(r.message || "Moved & resolved.") : fail(r.message || "Move failed — flag kept open."); }
    catch (e) { fail(e?.response?.data?.detail || "Move failed."); }
  };
  const sendBack = async () => {
    try { const r = await doFix("send_back"); r.ok ? finish(r.message || "Sent back & resolved.") : fail(r.message || "Failed."); }
    catch (e) { fail(e?.response?.data?.detail || "Failed."); }
  };
  const confirmEmpty = async () => {
    try { const r = await doFix("confirm_empty"); r.ok ? finish("Confirmed empty & resolved.") : fail("Failed."); }
    catch (e) { fail(e?.response?.data?.detail || "Failed."); }
  };
  const falseAlarm = async () => {
    try { await doResolve("false alarm — verified correct"); finish("Resolved (false alarm)."); }
    catch { fail("Couldn't resolve."); }
  };

  const isZero = detail?.kind === "zero_parts";
  const parts = detail?.parts_in_tote || [];
  const from = detail?.from_location, to = detail?.attempted_destination;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-kb overflow-y-auto" data-testid="problem-move-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detail && <span className={`rounded text-[10px] font-black uppercase px-2 py-0.5 ${isZero ? "bg-amber-200 text-amber-900" : "bg-red-200 text-red-900"}`}>{isZero ? "0 parts" : "error"}</span>}
            <span className="font-mono">{detail?.tote?.short_code || detail?.container_code || "Problem move"}</span>
          </DialogTitle>
        </DialogHeader>

        {loading || !detail ? (
          <div className="py-10 flex justify-center text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : detail.resolved ? (
          <div className="py-8 text-center text-emerald-600 font-bold" data-testid="drawer-resolved"><Check className="w-6 h-6 inline mr-1" />Already resolved</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="text-slate-600">{detail.reason}</div>

            {/* Move */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Move</div>
              <div className="flex items-center gap-2 font-semibold flex-wrap">
                <span>{fullLabel(from) || from?.short_code || "—"}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{fullLabel(to) || to?.short_code || detail.dest_code || detail.location_scan || "—"}</span>
              </div>
              <div className="mt-1 text-slate-500">Tote now at <b>{fullLabel(detail.tote?.current_location) || detail.tote?.current_location?.short_code || "—"}</b></div>
              {(detail.container_scan || detail.location_scan) && <div className="mt-1 font-mono text-[11px] text-slate-400 truncate">scanned: {detail.container_scan || "?"} → {detail.location_scan || "?"}</div>}
              <div className="text-[11px] text-slate-400">{detail.by} · {detail.at ? new Date(detail.at).toLocaleString() : ""}</div>
            </div>

            {/* Parts */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">In the tote now · {detail.part_count}</div>
              {parts.length === 0 ? <div className="text-slate-500">Empty right now.</div> : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {parts.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                        {p.photo ? <AuthImg fileId={p.photo} thumb alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-slate-400" />}
                      </div>
                      <span className="font-mono font-bold">{p.short_code}</span>
                      <span className="text-slate-500 truncate">{p.name || ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lots + notes */}
            {(detail.lots || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 p-3" data-testid="drawer-lots">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Lot(s) & dismantler notes</div>
                <div className="space-y-2">
                  {detail.lots.map((l) => (
                    <div key={l.id}>
                      <div className="font-semibold">{l.batch_number || l.label || "Lot"}{l.donor_label ? ` · ${l.donor_label}` : ""}</div>
                      {l.notes ? <div className="mt-0.5 text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-2 text-[13px] max-h-32 overflow-y-auto"><StickyNote className="w-3.5 h-3.5 inline mr-1 text-amber-500" />{l.notes}</div> : <div className="text-slate-400 text-[12px]">No dismantler notes.</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-1">
              <div className="flex gap-2">
                <ScanTextInput testid="drawer-dest-input" value={dest} onChange={setDest}
                  placeholder="Correct destination bin code" className="flex-1" inputClassName="h-10" />
                <Button data-testid="drawer-move" onClick={move} disabled={busy} className="bg-[#B01E28] hover:bg-[#8B1820] text-white shrink-0"><ArrowRightLeft className="w-4 h-4 mr-1.5" />Move</Button>
              </div>
              {detail.can_send_back && (
                <Button data-testid="drawer-sendback" variant="outline" onClick={sendBack} disabled={busy} className="w-full justify-start"><MapPin className="w-4 h-4 mr-1.5" />Send back to {detail.from_location?.short_code || "origin"}</Button>
              )}
              <Button data-testid="drawer-open-tote" variant="outline" onClick={() => detail.tote?.id && navigate(`/scan/loc/${detail.tote.id}`)} disabled={!detail.tote?.id} className="w-full justify-start"><Boxes className="w-4 h-4 mr-1.5" />Open the tote</Button>
              {isZero && <Button data-testid="drawer-confirm-empty" variant="outline" onClick={confirmEmpty} disabled={busy} className="w-full justify-start"><PackageOpen className="w-4 h-4 mr-1.5" />Confirm empty — resolve</Button>}
              <Button data-testid="drawer-fix-at-bin" variant="outline" onClick={() => navigate(`/floor/problem/${detail.id}`)} className="w-full justify-start text-[#E0781A] border-[#E0781A]/40"><Smartphone className="w-4 h-4 mr-1.5" />Fix at the bin (tablet)</Button>
              <button data-testid="drawer-false-alarm" onClick={falseAlarm} disabled={busy} className="w-full h-9 rounded-md text-slate-500 text-xs font-bold hover:bg-slate-50 disabled:opacity-50">False alarm — resolve</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
